import React from 'react';
import { useFormik } from 'formik';
import { TextField, Typography, Box, Alert } from '@mui/material';
import { Step3Schema } from '../../validations/user';
import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';
import BorderedButton from '../common/buttons/BorderedButton';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';
import { sendConfirmationCode } from '../../utils/user';
import useBackendApi from '../../hooks/useBackendApi';

interface Step3Props {
  onSubmit: (values: any, { setSubmitting }: any) => void;
  email: string;
  isSubmitting: boolean;
  onResendCode: ({ setResendSubmitting }: any, { setMessage }: any) => void;
}

interface MessageI {
  text: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const SignupStep3Confirmation: React.FC<Step3Props> = ({ onSubmit, email, isSubmitting, onResendCode }) => {
  const { theme } = useThemeContext();
  const [message, setMessage] = React.useState<MessageI>({} as MessageI);
  const [resendSubmitting, setResendSubmitting] = React.useState(false);
  const { apiFetchRequest } = useBackendApi();

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      confirmationCode: '',
    },
    validationSchema: Step3Schema,
    onSubmit: onSubmit,
  });

  const resendCode = async () => {
    onResendCode(setResendSubmitting, setMessage);
  };

  return (
    <Box
      sx={{
        mt: 1,
        mb: 3,
        padding: { xs: '0 24px', sm: '0 24px', md: '0' },
      }}
    >
      {message?.text && (
        <Alert severity={message.severity} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      <Typography
        variant="h4"
        sx={{
          textAlign: { xs: 'center', sm: 'left' },
          color: theme.custom.components.signupStep3Confirmation.typography.color,
        }}
      >
        Confirm Your Email
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          textAlign: { xs: 'center', sm: 'left' },
          color: theme.custom.components.signupStep3Confirmation.typography.color,
        }}
      >
        Please enter the confirmation code sent to your email: {email}
      </Typography>
      <TextField
        sx={{ mb: 2, paddingRight: { md: '40%', sm: '20%', xs: '0' } }}
        {...formik.getFieldProps('confirmationCode')}
        label="Confirmation Code"
        placeholder="Enter confirmation code"
        fullWidth
        error={formik.touched.confirmationCode && Boolean(formik.errors.confirmationCode)}
        helperText={formik.touched.confirmationCode && formik.errors.confirmationCode}
      />
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'center', sm: 'flex-start' },
          maxWidth: { xs: '100%', sm: 'auto' },
          padding: { xs: '0 24px', sm: '0' },
        }}
      >
        <PrimaryBlueButton
          isLoading={isSubmitting}
          onClick={() => formik.handleSubmit()}
          buttonText="Confirm"
          sxProps={{
            marginBottom: { xs: '10px', sm: '10px' },
          }}
          additionalProps={{
            disabled: !formik.isValid || isSubmitting,
          }}
        />
        <BorderedButton
          isLoading={resendSubmitting}
          onClick={resendCode}
          buttonText="Resend Code"
          sxProps={{
            height: '42px',
            marginLeft: { xs: '0', sm: '10px' },
          }}
          additionalProps={{
            disabled: resendSubmitting,
          }}
        />
      </Box>
    </Box>
  );
};

export default SignupStep3Confirmation;
